import { Component, Input, OnDestroy, inject } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { HttpClient } from '@angular/common/http';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { GenericFormlyFormComponent } from '@intalio/formly-builder/bootstrap';
import { RequestAction } from '../../../models/form-models';
import { BACKEND_API_BASE_URL } from '../../../config/app-settings.tokens';

/**
 * Task Viewer - Tab 3: Task History
 * Displays timeline of all actions and changes to the task
 * Shows read-only detail form when expanding an action
 */
@Component({
  selector: 'app-task-history-tab',
  standalone: true,
  templateUrl: './task-history-tab.component.html',
  styleUrls: ['./task-history-tab.component.scss'],
  imports: [
    CommonModule,
    DatePipe,
    TranslateModule,
    GenericFormlyFormComponent,
  ]
})
export class TaskHistoryTabComponent implements OnDestroy {
  @Input() previousActions: RequestAction[] = [];
  @Input() pageType: 'inbox' | 'sent' | 'completed' | 'myRequests' = 'inbox';
  @Input() requestId: string | null = null;
  @Input() taskId: string | null = null;

  private http = inject(HttpClient);
  private translate = inject(TranslateService);
  private readonly backendApiBaseUrl = inject(BACKEND_API_BASE_URL);
  private destroy$ = new Subject<void>();

  expandedActionId: number | null = null;
  /** Raw form definition JSON for the expanded history entry */
  expandedFormDefinition: any = null;
  expandedFormModel: any = {};
  isLoadingForm: boolean = false;
  formLoadErrors: string[] = [];
  currentLanguage: string = 'en';

  constructor() {
    this.currentLanguage = this.translate.currentLang || 'en';
  }

  toggleExpand(index: number, taskId: string): void {
    if (this.expandedActionId === index) {
      this.expandedActionId = null;
      this.expandedFormDefinition = null;
      this.expandedFormModel = {};
    } else {
      this.expandedActionId = index;
      this.loadActionForm(index, taskId);
    }
  }

  isExpanded(index: number): boolean {
    return this.expandedActionId === index;
  }

  private loadActionForm(actionIndex: number, taskId: string): void {
    if (!this.requestId || !taskId) {
      this.formLoadErrors = ['Missing requestId or taskId'];
      return;
    }

    this.isLoadingForm = true;
    this.formLoadErrors = [];

    const apiUrl = `${this.backendApiBaseUrl}/request/id/${this.requestId}/task/${taskId}/form`;
    this.http.get<any>(apiUrl)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: any) => {
          const formDesigner = response.formDesigner || {};
          const formJson = formDesigner.formJson || [];
          this.expandedFormDefinition = Array.isArray(formJson) ? formJson : [];
          this.expandedFormModel = response.formData || {};
          this.isLoadingForm = false;
        },
        error: (error: any) => {
          console.error('[TaskHistoryTab] Error loading form:', error);
          this.formLoadErrors = ['Failed to load form details'];
          this.isLoadingForm = false;
        }
      });
  }

  getActionLabel(action: string): string {
    const labelMap: { [key: string]: string } = {
      'Approve': 'Approved',
      'Approved': 'Approved',
      'Return': 'Returned',
      'Reject': 'Rejected',
      'SaveAsDraft': 'Saved as Draft',
      'Submit': 'Submitted',
      'Forward': 'Forwarded',
      'Delegate': 'Delegated',
      'Accept': 'Accepted',
      'Withdraw': 'Withdrawn'
    };
    return labelMap[action] || action;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
